import React, { useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import clsx from "clsx";
import { fade, makeStyles } from "@material-ui/core/styles";
import {
  Checkbox as MuiCheckbox,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { green, grey, orange, red } from "@material-ui/core/colors";
import UndoIcon from "@material-ui/icons/Undo";
import GlobalContext from "Jarvis/JarvisContexts/GlobalContext";
import Button from "Jarvis/JarvisComponents/Button";
import Checkbox from "Jarvis/JarvisComponents/Checkbox";
import Select from "Jarvis/JarvisComponents/Select";
import {
  ClientSystemPatronClientRetrieve,
  ClientSystemPatronClientSystemRetrieve,
  ClientSystemPatronRetrieveByPatron,
} from "Jarvis/JarvisSystems/Lic/Services/ClientSystemPatronService";

const DAY = 24 * 60 * 60 * 1000;
const EXPIRES_SOON_DAYS = 30;
const FILTER_FROM = 7; // the system filter appears once a client has this many systems

const clearIconStyle = {
  position: "absolute",
  right: 8,
  top: "50%",
  transform: "translateY(-50%)",
  cursor: "pointer",
  color: "#888",
  fontSize: 16,
  lineHeight: 1,
};

const useStyles = makeStyles((theme) => ({
  topBar: {
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: theme.spacing(1, 3),
    marginBottom: theme.spacing(2),
  },
  clientSelect: { flex: "1 1 320px", maxWidth: 560 },
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(280px, 1fr)",
    gap: theme.spacing(2),
    alignItems: "start",
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  panel: {
    border: `1px solid ${grey[300]}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    minWidth: 0,
  },
  panelHeader: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${grey[200]}`,
  },
  panelTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: grey[900],
    marginInlineEnd: "auto",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  counter: {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    backgroundColor: fade(theme.palette.primary.main, 0.08),
    borderRadius: 12,
    padding: "2px 10px",
    whiteSpace: "nowrap",
  },
  panelBody: { padding: theme.spacing(2) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  filter: { position: "relative", flex: "0 1 260px" },
  toolbarSpacer: { marginInlineStart: "auto", display: "flex", gap: theme.spacing(1) },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
  },
  tile: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5, 1.25, 0.5),
    border: `1px solid ${grey[300]}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    userSelect: "none",
    outline: "none",
    transition: "border-color .15s, background-color .15s, box-shadow .15s",
    "&:hover": {
      borderColor: theme.palette.primary.light,
      boxShadow: "0 2px 6px rgba(0, 0, 0, .08)",
    },
    "&:focus-visible": {
      boxShadow: `0 0 0 2px ${fade(theme.palette.primary.main, 0.5)}`,
    },
  },
  tileLinked: {
    borderColor: theme.palette.primary.main,
    backgroundColor: fade(theme.palette.primary.main, 0.05),
  },
  tileNew: { borderColor: green[600], backgroundColor: green[50] },
  tileRemoved: {
    borderColor: red[400],
    borderStyle: "dashed",
    backgroundColor: red[50],
  },
  tileCheckbox: { padding: 4, marginTop: -2 },
  tileText: { minWidth: 0, flex: 1 },
  tileName: {
    fontWeight: 600,
    fontSize: "0.85rem",
    color: grey[900],
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  tileNameRemoved: { textDecoration: "line-through", color: grey[600] },
  tilePeriod: { fontSize: "0.75rem", color: grey[600], marginTop: 2 },
  badges: { display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.68rem",
    fontWeight: 600,
    lineHeight: "18px",
    padding: "0 8px",
    borderRadius: 9,
    whiteSpace: "nowrap",
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
  statusActive: { color: green[800], backgroundColor: green[100] },
  statusSoon: { color: orange[900], backgroundColor: orange[100] },
  statusExpired: { color: red[800], backgroundColor: red[100] },
  statusNone: { color: grey[700], backgroundColor: grey[200] },
  badgeNew: { color: "#fff", backgroundColor: green[600] },
  badgeRemoved: { color: "#fff", backgroundColor: red[600] },
  empty: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    color: grey[600],
    fontSize: "0.85rem",
    border: `1px dashed ${grey[300]}`,
    borderRadius: 8,
  },
  loading: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
  group: {
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${grey[200]}`,
    "&:first-child": { paddingTop: 0 },
    "&:last-child": { borderBottom: 0, paddingBottom: 0 },
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "100%",
    padding: 0,
    border: 0,
    background: "none",
    font: "inherit",
    textAlign: "start",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.82rem",
    color: grey[900],
    "&:hover": { color: theme.palette.primary.main },
  },
  groupHeaderSelected: { color: theme.palette.primary.main },
  groupName: { overflow: "hidden", textOverflow: "ellipsis" },
  groupCount: {
    marginInlineStart: "auto",
    minWidth: 22,
    textAlign: "center",
    fontSize: "0.72rem",
    borderRadius: 10,
    padding: "0 6px",
    backgroundColor: grey[200],
    color: grey[800],
  },
  chips: { display: "flex", flexWrap: "wrap", gap: 6, marginTop: theme.spacing(1) },
  chipNew: { borderColor: green[600], color: green[800], backgroundColor: green[50] },
  chipRemoved: {
    borderColor: red[300],
    color: red[700],
    backgroundColor: red[50],
    "& .MuiChip-label": { textDecoration: "line-through" },
  },
  pending: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    fontSize: "0.78rem",
    color: orange[900],
    backgroundColor: orange[50],
    border: `1px solid ${orange[200]}`,
  },
}));

const toLink = (item, isNew) => ({
  ..._.pick(item, [
    "ClientSystemPatronId",
    "ClientSystemId",
    "VT",
    "ClientId",
    "ClientLangDescription",
    "SystemId",
    "SystemLangDescription",
    "StartDateTime",
    "EndDateTime",
  ]),
  IsNew: isNew,
});

const parseDate = (value) =>
  value ? Date.parse(String(value).replace(" ", "T")) : NaN;

const licenceStatus = (item) => {
  const start = parseDate(item.StartDateTime);
  const end = parseDate(item.EndDateTime);
  if (Number.isNaN(start) || Number.isNaN(end)) return "none";
  const now = Date.now();
  if (now < start) return "notStarted";
  if (now > end) return "expired";
  if (end - now < EXPIRES_SOON_DAYS * DAY) return "soon";
  return "active";
};

const datePart = (value) => (value ? String(value).slice(0, 10) : "");

/**
 * Licence step of the account side form: which systems of its clients the account side can use
 * (Lic_Tb_ClientSystemPatron). The user picks a client and ticks its systems; nothing is saved here -
 * the links travel with the patron save, as ClientSystems (new links) and ClientSystemsRemoved
 * (deleted links).
 *
 * patronData.ClientSystemPatrons  - the links: the stored ones plus new ones (IsNew)
 * patronData.ClientSystemsRemoved - stored links the user took away
 */
export default function PatronLicence({
  control,
  isEditMode,
  langData,
  patronData,
  setPatronData,
  setValue,
}) {
  const classes = useStyles();
  const { PE } = useContext(GlobalContext) ?? {};
  const t = (key, fallback) => _.get(langData, key) ?? fallback;
  const pe = (key, fallback) => _.get(PE, `PubPublicElems.${key}`) ?? fallback;

  const relatedPatronIds = useMemo(
    () =>
      _.uniq(
        (patronData.PatronRelations ?? [])
          .filter((relation) => relation.IsActive !== false)
          .map((relation) => relation.PatronIdTo)
          .filter((patronId) => !_.isNil(patronId))
      ),
    [patronData.PatronRelations]
  );
  const [onlyRelated, setOnlyRelated] = useState(relatedPatronIds.length > 0);
  const [client, setClient] = useState(null);
  const [clientSystems, setClientSystems] = useState([]);
  const [isLoadingSystems, setIsLoadingSystems] = useState(false);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [filter, setFilter] = useState("");

  const links = patronData.ClientSystemPatrons ?? [];
  const removed = patronData.ClientSystemsRemoved ?? [];
  const linksReady = !_.isUndefined(patronData.ClientSystemPatrons);

  //#region ------------- loading -------------
  // The stored links are read once, the first time the step is opened.
  useEffect(() => {
    if (linksReady) return;
    if (!isEditMode) {
      setPatronData((patronData) => ({
        ...patronData,
        ClientSystemPatrons: [],
        ClientSystemsRemoved: [],
      }));
      return;
    }
    (async () => {
      setIsLoadingLinks(true);
      const { ErrorHandling, Data } = await ClientSystemPatronRetrieveByPatron({
        PatronId: patronData.PatronId,
        VT: patronData.VT,
      });
      setIsLoadingLinks(false);
      if (_.get(ErrorHandling, "IsSuccessful"))
        setPatronData((patronData) => ({
          ...patronData,
          ClientSystemPatrons: (Data ?? []).map((item) => toLink(item, false)),
          ClientSystemsRemoved: [],
        }));
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open on a client straight away: the first one the account side is linked to, otherwise the
  // only client among its related account sides.
  useEffect(() => {
    if (!linksReady || client) return;
    const first = links[0];
    if (first) {
      selectClient(_.pick(first, ["ClientId", "ClientLangDescription"]));
      return;
    }
    if (!onlyRelated || relatedPatronIds.length === 0) return;
    (async () => {
      const { ErrorHandling, Data } = await ClientSystemPatronClientRetrieve({
        pageIndex: 1,
        pageSize: 2,
        patronIds: relatedPatronIds,
      });
      if (_.get(ErrorHandling, "IsSuccessful") && Data?.length === 1)
        selectClient(Data[0]);
    })();
  }, [linksReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeClient = async (value) => {
    setClient(value?.ClientId ? value : null);
    setClientSystems([]);
    setFilter("");
    if (!value?.ClientId) return;
    setIsLoadingSystems(true);
    const { ErrorHandling, Data } = await ClientSystemPatronClientSystemRetrieve(
      { ClientId: value.ClientId }
    );
    setIsLoadingSystems(false);
    if (_.get(ErrorHandling, "IsSuccessful")) setClientSystems(Data ?? []);
  };

  const selectClient = (value) => {
    setValue("LicenceClient", value);
    handleChangeClient(value);
  };
  //#endregion

  //#region ------------- link state -------------
  const stateOf = (clientSystemId) => {
    const link = links.find((item) => item.ClientSystemId === clientSystemId);
    if (link) return link.IsNew ? "new" : "linked";
    if (removed.some((item) => item.ClientSystemId === clientSystemId))
      return "removed";
    return "none";
  };

  // Links or unlinks the given client systems. Unlinking a stored link remembers it as removed
  // (so the save deletes it); relinking it takes it back instead of adding a new one.
  const setLinked = (items, linked) =>
    setPatronData((patronData) => {
      let nextLinks = [...(patronData.ClientSystemPatrons ?? [])];
      let nextRemoved = [...(patronData.ClientSystemsRemoved ?? [])];
      items.forEach((item) => {
        const id = item.ClientSystemId;
        const link = nextLinks.find((l) => l.ClientSystemId === id);
        const gone = nextRemoved.find((l) => l.ClientSystemId === id);
        if (linked && !link) {
          if (gone) {
            nextRemoved = nextRemoved.filter((l) => l !== gone);
            nextLinks.push(gone);
          } else nextLinks.push(toLink(item, true));
        } else if (!linked && link) {
          nextLinks = nextLinks.filter((l) => l !== link);
          if (!link.IsNew) nextRemoved.push(link);
        }
      });
      return {
        ...patronData,
        ClientSystemPatrons: nextLinks,
        ClientSystemsRemoved: nextRemoved,
      };
    });
  //#endregion

  //#region ------------- derived -------------
  // The client's licensed systems, plus any of its links whose licence is no longer current, so
  // every link can still be seen and removed.
  const tiles = client
    ? _.sortBy(
        [
          ...clientSystems,
          ...[...links, ...removed].filter(
            (link) =>
              link.ClientId === client.ClientId &&
              !clientSystems.some((s) => s.ClientSystemId === link.ClientSystemId)
          ),
        ],
        (item) => (item.SystemLangDescription ?? "").toLowerCase()
      )
    : [];

  const visibleTiles = filter.trim()
    ? tiles.filter((item) =>
        (item.SystemLangDescription ?? "")
          .toLowerCase()
          .includes(filter.trim().toLowerCase())
      )
    : tiles;
  const linkedCount = tiles.filter((item) =>
    ["linked", "new"].includes(stateOf(item.ClientSystemId))
  ).length;

  const groups = _.sortBy(
    _.map(
      _.groupBy(
        [
          ...links.map((link) => ({ ...link, state: link.IsNew ? "new" : "linked" })),
          ...removed.map((link) => ({ ...link, state: "removed" })),
        ],
        "ClientId"
      ),
      (items) => ({
        ClientId: items[0].ClientId,
        ClientLangDescription: items[0].ClientLangDescription,
        count: items.filter((item) => item.state !== "removed").length,
        items: _.sortBy(items, (item) => (item.SystemLangDescription ?? "").toLowerCase()),
      })
    ),
    (group) => (group.ClientLangDescription ?? "").toLowerCase()
  );
  const toAdd = links.filter((link) => link.IsNew).length;
  const toRemove = removed.length;
  //#endregion

  //#region ------------- rendering -------------
  const statusBadge = (item) => {
    const status = licenceStatus(item);
    const map = {
      active: [classes.statusActive, t("LicenceStatusActive", "Active")],
      soon: [classes.statusSoon, t("LicenceStatusExpiresSoon", "Expires soon")],
      expired: [classes.statusExpired, t("LicenceStatusExpired", "Expired")],
      notStarted: [classes.statusNone, t("LicenceStatusNotStarted", "Not started")],
      none: [classes.statusNone, t("LicenceStatusNone", "No current licence")],
    };
    const [className, label] = map[status];
    return (
      <span className={clsx(classes.badge, className)}>
        <span className={classes.dot} />
        {label}
      </span>
    );
  };

  const renderTile = (item) => {
    const state = stateOf(item.ClientSystemId);
    const checked = state === "linked" || state === "new";
    const toggle = () => setLinked([item], !checked);
    return (
      <div
        key={item.ClientSystemId}
        role="checkbox"
        aria-checked={checked}
        tabIndex={0}
        className={clsx(
          classes.tile,
          state === "linked" && classes.tileLinked,
          state === "new" && classes.tileNew,
          state === "removed" && classes.tileRemoved
        )}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <MuiCheckbox
          className={classes.tileCheckbox}
          checked={checked}
          color="primary"
          size="small"
          tabIndex={-1}
          disableRipple
        />
        <div className={classes.tileText}>
          <div
            className={clsx(
              classes.tileName,
              state === "removed" && classes.tileNameRemoved
            )}
          >
            {item.SystemLangDescription}
          </div>
          {(item.StartDateTime || item.EndDateTime) && (
            <div
              className={classes.tilePeriod}
              title={`${item.StartDateTime ?? ""} - ${item.EndDateTime ?? ""}`}
            >
              <bdi>{datePart(item.StartDateTime)}</bdi>
              {" - "}
              <bdi>{datePart(item.EndDateTime)}</bdi>
            </div>
          )}
          <div className={classes.badges}>
            {statusBadge(item)}
            {state === "new" && (
              <span className={clsx(classes.badge, classes.badgeNew)}>
                {t("LicenceNew", "New")}
              </span>
            )}
            {state === "removed" && (
              <span className={clsx(classes.badge, classes.badgeRemoved)}>
                {t("LicenceWillBeRemoved", "Will be removed")}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSystems = () => {
    if (!client)
      return (
        <div className={classes.empty}>
          {t(
            "LicenceSelectClientHint",
            "Choose a client to see the systems it is licensed for."
          )}
        </div>
      );
    if (isLoadingSystems)
      return (
        <div className={classes.loading}>
          <CircularProgress size={28} />
        </div>
      );
    if (tiles.length === 0)
      return (
        <div className={classes.empty}>
          {t("LicenceNoClientSystems", "This client has no licensed systems.")}
        </div>
      );
    const anyUnlinked = visibleTiles.some(
      (item) => !["linked", "new"].includes(stateOf(item.ClientSystemId))
    );
    const anyLinked = visibleTiles.some((item) =>
      ["linked", "new"].includes(stateOf(item.ClientSystemId))
    );
    return (
      <>
        <div className={classes.toolbar}>
          {tiles.length >= FILTER_FROM && (
            <div className={classes.filter}>
              <input
                type="text"
                className="form-control form-control-sm"
                style={{ paddingRight: 28 }}
                placeholder={pe("Filter", "Filter")}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter && (
                <span
                  role="button"
                  title={pe("Clear", "Clear")}
                  onClick={() => setFilter("")}
                  style={clearIconStyle}
                >
                  &times;
                </span>
              )}
            </div>
          )}
          <div className={classes.toolbarSpacer}>
            <Button
              label={pe("SelectAll", "Select All")}
              variant="outlined"
              color="primary"
              disabled={!anyUnlinked}
              onClick={() =>
                setLinked(
                  visibleTiles.filter(
                    (item) => !["linked", "new"].includes(stateOf(item.ClientSystemId))
                  ),
                  true
                )
              }
            />
            <Button
              label={pe("Clear", "Clear")}
              variant="outlined"
              disabled={!anyLinked}
              onClick={() => setLinked(visibleTiles, false)}
            />
          </div>
        </div>
        {visibleTiles.length === 0 ? (
          <div className={classes.empty}>
            {t("LicenceNoFilterMatch", "No system matches the filter.")}
          </div>
        ) : (
          <div className={classes.grid}>{visibleTiles.map(renderTile)}</div>
        )}
      </>
    );
  };

  const renderSummary = () => {
    if (isLoadingLinks)
      return (
        <div className={classes.loading}>
          <CircularProgress size={28} />
        </div>
      );
    if (groups.length === 0)
      return (
        <div className={classes.empty}>
          {t("LicenceNoLinks", "This account side has no system access yet.")}
        </div>
      );
    return groups.map((group) => (
      <div key={group.ClientId} className={classes.group}>
        <button
          type="button"
          className={clsx(
            classes.groupHeader,
            client?.ClientId === group.ClientId && classes.groupHeaderSelected
          )}
          onClick={() =>
            selectClient(_.pick(group, ["ClientId", "ClientLangDescription"]))
          }
        >
          <span className={classes.groupName}>{group.ClientLangDescription}</span>
          <span className={classes.groupCount}>{group.count}</span>
        </button>
        <div className={classes.chips}>
          {group.items.map((item) =>
            item.state === "removed" ? (
              <Chip
                key={item.ClientSystemId}
                size="small"
                variant="outlined"
                className={classes.chipRemoved}
                label={item.SystemLangDescription}
                deleteIcon={<UndoIcon />}
                onDelete={() => setLinked([item], true)}
              />
            ) : (
              <Chip
                key={item.ClientSystemId}
                size="small"
                variant="outlined"
                color={item.state === "linked" ? "primary" : "default"}
                className={clsx(item.state === "new" && classes.chipNew)}
                label={item.SystemLangDescription}
                onDelete={() => setLinked([item], false)}
              />
            )
          )}
        </div>
      </div>
    ));
  };

  return (
    <div>
      <div className={classes.topBar}>
        <div className={classes.clientSelect}>
          <Select
            label={t("LicClient", "Client")}
            control={control}
            name="LicenceClient"
            service={ClientSystemPatronClientRetrieve}
            serviceParams={{ patronIds: onlyRelated ? relatedPatronIds : [] }}
            displayFieldName="ClientLangDescription"
            valueFieldName="ClientId"
            searchFieldNames={["ClientLangDescription"]}
            onChange={handleChangeClient}
          />
        </div>
        {relatedPatronIds.length > 0 && (
          <Checkbox
            label={t(
              "OnlyRelatedAccountSideClients",
              "Only clients of related account sides"
            )}
            name="LicenceOnlyRelatedClients"
            value={onlyRelated}
            onChange={(e, checked) => setOnlyRelated(Boolean(checked))}
          />
        )}
      </div>

      <div className={classes.layout}>
        <section className={classes.panel}>
          <div className={classes.panelHeader}>
            <span className={classes.panelTitle}>
              {client?.ClientLangDescription ?? t("LicClientSystem", "Systems")}
            </span>
            {client && tiles.length > 0 && (
              <span className={classes.counter}>
                {t("LicenceLinkedOfTotal", "{0} of {1} linked")
                  .replace("{0}", linkedCount)
                  .replace("{1}", tiles.length)}
              </span>
            )}
          </div>
          <div className={classes.panelBody}>{renderSystems()}</div>
        </section>

        <aside className={classes.panel}>
          <div className={classes.panelHeader}>
            <span className={classes.panelTitle}>
              {t("LicenceLinkedClients", "Linked clients")}
            </span>
          </div>
          <div className={classes.panelBody}>
            {renderSummary()}
            {(toAdd > 0 || toRemove > 0) && (
              <div className={classes.pending}>
                {t(
                  "LicencePendingChanges",
                  "{0} to add, {1} to remove - saved when you press Save"
                )
                  .replace("{0}", toAdd)
                  .replace("{1}", toRemove)}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
  //#endregion
}
